import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { Portal } from "@/types";
import { FormProvider, SelectBox } from "@/components/form";
import { useMasterLlmModels } from "../hooks/useMasterLlmModels";

export interface EmbeddingFormValues {
  vision_model: string;
}

interface EmbeddingTabProps {
  portal: Portal;
  onSave: (data: EmbeddingFormValues) => void;
  isUpdating: boolean;
}

const EmbeddingTab: React.FC<EmbeddingTabProps> = ({ portal, onSave, isUpdating }) => {
  const { visionModels, isLoading: isLoadingModels } = useMasterLlmModels();

  const visionModelOptions = useMemo(() => {
    return visionModels.map((m) => ({ label: m.model_name, value: m.model_id }));
  }, [visionModels]);

  const methods = useForm<EmbeddingFormValues>({
    defaultValues: {
      vision_model: portal.settings?.vision_model ?? "",
    },
  });

  useEffect(() => {
    methods.reset({
      vision_model: portal.settings?.vision_model ?? "",
    });
  }, [portal, methods]);

  return (
    <Stack spacing={3}>
      {/* Embedding — Read-only (set during provisioning) */}
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
        Embedding Configuration
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Embedding model is set during provisioning and cannot be changed.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Embedding provider"
            value={portal.settings?.embedding_provider ?? "—"}
            fullWidth
            slotProps={{ input: { readOnly: true } }}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Embedding model"
            value={portal.settings?.embedding_model ?? "—"}
            fullWidth
            slotProps={{ input: { readOnly: true } }}
            disabled
          />
        </Grid>
      </Grid>

      {/* Vision Model — Editable */}
      <FormProvider methods={methods} onSubmit={onSave}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Vision Model
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Used for PDF image extraction before sending to embedding.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SelectBox
                name="vision_model"
                label="Vision model (OpenAI)"
                fullWidth
                disabled={isUpdating || isLoadingModels}
                options={visionModelOptions}
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isUpdating}>
              {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save Vision Model"}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default EmbeddingTab;
